import React from 'react'
import PropTypes from 'prop-types'

import { useAuthStore } from '../../store/auth'
import { useDependencies } from '../../store/dependencies'
import { getDependencyCall } from '../../lib/api/get'
import { useUpdateCall } from '../../lib/api/update'

import closeBtn from '../../assets/images/closeBtn.png'

/**
 * Renders a modal for creating and sending a new message. The modal includes
 * a form with fields for subject and message content. It also provides a submit
 * button that triggers the message send action.
 *
 * @module messages/UpdateChat
 * @description Renders a modal for creating and sending a new message. The modal includes
 * a form with fields for subject and message content. It also provides a submit
 * button that triggers the message send action.
 * @example
 * const setNewMessageModal = () => {};
 * const handleSubmit = () => {};
 * const sendMessage = () => {};
 * const register = () => {};
 * const createCallMutation = { isLoading: false };
 *
 * return (
 *   <MessageModal
 *     setNewMessageModal={setNewMessageModal}
 *     handleSubmit={handleSubmit}
 *     sendMessage={sendMessage}
 *     register={register}
 *     createCallMutation={createCallMutation}
 *   />
 * );
 *
 * @param {function} setNewMessageModal - Function to toggle the visibility of the modal.
 * @param {function} handleSubmit - Function from react-hook-form to handle form submission.
 * @param {function} sendMessage - Function to handle the sending of the message.
 * @param {function} register - Function from react-hook-form to register input fields.
 * @param {Object} createCallMutation - Object containing the mutation state for the message send request.
 * @param {boolean} createCallMutation.isLoading - Boolean indicating if the send message request is loading.
 * @returns {React.Element} The rendered message modal component.
 */
export const UpdateChat = ({
  isLocalEnvironment,
  setNewMessageModal,
  getUsers = false,
  activeChat,
}) => {
  const appState = useAuthStore((state) => state)
  const updateCallMutation = useUpdateCall()
  const { setDependency } = useDependencies()
  const [gettingDeps, setGettingDeps] = React.useState(false)
  const stateVar = { ...appState }
  const getDeps = async () => {
    setGettingDeps(true)
    if (stateVar.dependeciesFormAPIS) {
      stateVar.dependeciesFormAPIS['users'] = await getDependencyCall(
        isLocalEnvironment,
        'users',
        '&isDependency=true',
      )
    } else {
      stateVar['dependeciesFormAPIS'] = {}
      stateVar.dependeciesFormAPIS['users'] = await getDependencyCall(
        isLocalEnvironment,
        'users',
        '&isDependency=true',
      )
    }
    setDependency(stateVar, stateVar.role, stateVar.roleId)
    setGettingDeps(false)
  }

  React.useEffect(() => {
    if (getUsers) {
      getDeps()
    }
  }, [])

  const submitHandler = async (e) => {
    e.preventDefault()
    const dataForm = new FormData(e.target)
    const data = { chatSubject: dataForm.get('subject') }
    await updateCallMutation.mutateAsync({
      url: isLocalEnvironment + '/updateData',
      apiEndpoint: 'chats',
      data: data, //data,
      id: activeChat.chatId,
      messageTitle: 'Chats',
      message: 'Chat updated successfully!',
    })
  }
  return (
    <div
      id={`signupModals${gettingDeps}`}
      className="trtui-modal trtui-fade trtui-show"
      tabIndex={-1}
      style={{ display: 'block', backgroundColor: 'rgba(55,55,55,0.3)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="trtui-modal-dialog trtui-modal-dialog-centered">
        <div className="trtui-modal-content trtui-border-0 trtui-overflow-hidden">
          <div className="trtui-modal-header trtui-p-3">
            <h4 className="trtui-card-title trtui-mb-0">Update Group</h4>
            <button
              type="button"
              className="trtui-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              style={{
                '--vz-btn-close-color': '#000',
                '--vz-btn-close-bg': `url(${closeBtn})`,
                '--vz-btn-close-opacity': '0.5',
                '--vz-btn-close-hover-opacity': '0.75',
                '--vz-btn-close-focus-shadow': '0 0 1 0.25rem rgba(var(--vz-primary-rgb), 0.25)',
                '--vz-btn-close-focus-opacity': 1,
                '--vz-btn-close-disabled-opacity': '0.25',
                '--vz-btn-close-white-filter': 'invert(1) grayscale(100%) brightness(200%)',
                WebkitBoxSizing: 'content-box',
                boxSizing: 'content-box',
                width: '1em',
                height: '1em',
                padding: '0.25em 0.25em',
                color: 'var(--vz-btn-close-color)',
                background: 'transparent var(--vz-btn-close-bg) center/1em auto no-repeat',
                border: 0,
                borderRadius: '0.25rem',
                opacity: 'var(--vz-btn-close-opacity)',
              }}
              onClick={() => setNewMessageModal(false)}
            />
          </div>
          <div className="trtui-modal-body">
            <form className="row" onSubmit={(e) => submitHandler(e)}>
              <div className="trtui-mb-3">
                <label htmlFor="fullName" className="trtui-form-label">
                  Subject
                </label>
                <input
                  type="text"
                  className="trtui-form-control"
                  placeholder="Subject"
                  name="subject"
                  defaultValue={activeChat.chatSubject}
                />
              </div>
              <div className="trtui-text-end">
                <button type="submit" className="trtui-btn trtui-btn-primary">
                  {updateCallMutation.isLoading ? (
                    <>
                      <span
                        className="trtui-spinner-border trtui-spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>{' '}
                      Updating...
                    </>
                  ) : (
                    <>Update Chat</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

UpdateChat.propTypes = {
  isLocalEnvironment: PropTypes.string,
  setNewMessageModal: PropTypes.func.isRequired,
  getUsers: PropTypes.bool,
  activeChat: PropTypes.object,
}
